import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { chatOptionsDropdownAttrs } from "@/models/chat/chatHelpers";
import { CHAT_MODEL_OPTIONS } from "@/models/chat/chatModels";
import { twMerge } from "@/utils/twMerge";

import type { ChatModel } from "@/models/chat/ChatModel";

export const ChatModelSelector = observer(function ChatModelSelector({
  chat,
}: {
  chat: ChatModel;
}) {
  const t = useTranslations();
  const selectedModel = chat.selectedBotModel;

  const items: DropdownItem[] = CHAT_MODEL_OPTIONS.map((option) => ({
    title: option.label,
    caption: "description" in option ? option.description : undefined,
    icon: option.icon,
    active: option.id === selectedModel.id,
    onClick: () => chat.setSelectedBotModelId(option.id),
    className: "h-7.5 min-h-7.5 py-0 flex items-center",
  }));

  return (
    <Dropdown
      toggleButtonLabel={t("chat.modelSelector")}
      anchor={{ to: "top end", padding: 8, gap: 8 }}
      className={twMerge(
        "group flex items-center gap-1 text-[14px] tracking-tight h-6 rounded-8 pl-2.5 pr-1 leading-none",
        "bg-transparent text-gray-500 hover:bg-sidebar-button-hover-bg",
        "data-[open]:bg-sidebar-button-active-bg data-[open]:text-[var(--color-icon-active)]",
        chat.selectedBotModelSyncError && "outline-1 outline-error",
      )}
      itemsClassName="min-w-[300px] no-scrollbar"
      toggleButtonComponent={
        <>
          <span>{selectedModel.label}</span>
          <Icon
            name="ArrowDownLarge"
            className={twMerge(
              "h-4 w-4 shrink-0",
              "group-data-[open]:rotate-180 [--color-icon-secondary:var(--color-gray-500)] group-data-[open]:[--color-icon-secondary:var(--color-icon-active)]",
            )}
          />
        </>
      }
      items={items}
      {...chatOptionsDropdownAttrs(chat)}
    />
  );
});
